/**
 * Edge-aware placement for floating UI anchored to a point inside a
 * scrollable viewport (e.g., cursor labels, hover bubbles, double-click
 * input bars). Picks the first of four diagonal placements
 * (bottom-right, bottom-left, top-right, top-left) that keeps the
 * floating box fully inside the viewport rect (with a configurable
 * inset). When none fit (box is wider/taller than the viewport), the
 * result is clamped to viewport edges.
 *
 * Pure function — no DOM access, no React. Coordinates are in the same
 * frame of reference as the supplied viewport (typically client-rect).
 */

export type Placement =
  | "bottom-right"
  | "bottom-left"
  | "top-right"
  | "top-left";

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface PlacementInput {
  /** The point we're anchoring to (e.g., cursor tip in viewport coords). */
  anchor: Point;
  /** Measured size of the floating box. */
  size: Size;
  /** Viewport rect to stay inside. */
  viewport: Rect;
  /** Min distance from any viewport edge (px). */
  inset: number;
  /** Distance between the anchor point and the box edge (px).
   *  Use 0 if the box should be flush to the anchor (no offset). */
  anchorOffset: number;
  /** Regions the floating box should avoid when another diagonal fits. */
  exclusions?: Rect[];
}

export interface PlacementResult {
  /** Top-left of the box in the same coord space as `viewport`. */
  left: number;
  top: number;
  placement: Placement;
  /** True if the result was clamped to the viewport (no placement fit). */
  clamped: boolean;
}

export const PLACEMENT_ORDER: readonly Placement[] = [
  "bottom-right",
  "bottom-left",
  "top-right",
  "top-left",
];

function tryPlacement(
  placement: Placement,
  input: PlacementInput,
): { left: number; top: number; fits: boolean } {
  const { anchor, size, viewport, inset, anchorOffset } = input;
  const left =
    placement === "bottom-right" || placement === "top-right"
      ? anchor.x + anchorOffset
      : anchor.x - anchorOffset - size.width;
  const top =
    placement === "bottom-right" || placement === "bottom-left"
      ? anchor.y + anchorOffset
      : anchor.y - anchorOffset - size.height;

  const insideViewport =
    left >= viewport.left + inset &&
    top >= viewport.top + inset &&
    left + size.width <= viewport.right - inset &&
    top + size.height <= viewport.bottom - inset;
  const candidate = {
    left,
    top,
    right: left + size.width,
    bottom: top + size.height,
  };
  const avoidsExclusions = (input.exclusions ?? []).every(
    (exclusion) => overlapArea(candidate, exclusion) === 0,
  );

  return { left, top, fits: insideViewport && avoidsExclusions };
}

/** Whether one diagonal keeps the box inside the viewport and clear of the
 *  exclusions, for callers that keep a placement until it stops fitting. */
export function placementFits(
  placement: Placement,
  input: PlacementInput,
): boolean {
  return tryPlacement(placement, input).fits;
}

function overlapArea(a: Rect, b: Rect): number {
  const width = Math.max(
    0,
    Math.min(a.right, b.right) - Math.max(a.left, b.left),
  );
  const height = Math.max(
    0,
    Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top),
  );
  return width * height;
}

export function computePlacement(input: PlacementInput): PlacementResult {
  for (const placement of PLACEMENT_ORDER) {
    const r = tryPlacement(placement, input);
    if (r.fits) return { left: r.left, top: r.top, placement, clamped: false };
  }

  // Clamp every diagonal candidate and pick the one with the least overlap.
  // The box may still overflow when larger than the viewport, but its
  // top-left remains visible and avoidable content stays as clear as possible.
  const minLeft = input.viewport.left + input.inset;
  const minTop = input.viewport.top + input.inset;
  const maxLeft = input.viewport.right - input.inset - input.size.width;
  const maxTop = input.viewport.bottom - input.inset - input.size.height;

  const candidates = PLACEMENT_ORDER.map((placement, index) => {
    const candidate = tryPlacement(placement, { ...input, exclusions: [] });
    const left = Math.min(
      Math.max(candidate.left, minLeft),
      Math.max(maxLeft, minLeft),
    );
    const top = Math.min(
      Math.max(candidate.top, minTop),
      Math.max(maxTop, minTop),
    );
    const rect = {
      left,
      top,
      right: left + input.size.width,
      bottom: top + input.size.height,
    };
    const overlap = (input.exclusions ?? []).reduce(
      (total, exclusion) => total + overlapArea(rect, exclusion),
      0,
    );
    return { left, top, placement, overlap, index };
  });
  candidates.sort((a, b) => a.overlap - b.overlap || a.index - b.index);
  const best = candidates[0]!;
  return {
    left: best.left,
    top: best.top,
    placement: best.placement,
    clamped: true,
  };
}
